import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '@theme';
import { useSession } from '@/contexts/SessionContext';
import { CommissionCalendarModal } from '@/components/commission/CommissionCalendarModal';
import * as DS from '@styles/DesignSystem.styles';
import {
  FiHome,
  FiFileText,
  FiUsers,
  FiTrendingUp,
  FiSettings,
  FiCreditCard,
  FiLogOut,
  FiMenu,
  FiX,
  FiUser,
  FiFolder,
  FiMessageCircle,
  FiSearch,
  FiCheckCircle,
  FiClipboard,
  FiBarChart2,
  FiShield,
  FiActivity,
  FiCalendar,
  FiDollarSign,
} from 'react-icons/fi';
import logoKajom from '@/assets/images/LogoKajomCapital.png';
import logoPscreen from '@/assets/images/logo_pscreen.png';



const SidebarContainer = styled(DS.Sidebar) <{ $isOpen: boolean }>`
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  border-right: 1px solid ${theme.colors.border};
  z-index: 1000;
  transition: transform ${theme.transitions.normal};
  display: flex;
  flex-direction: column;

  @media (max-width: ${theme.breakpoints.lg}) {
    transform: translateX(${props => (props.$isOpen ? '0' : '-100%')});
    box-shadow: ${theme.shadows.lg};
  }
`;

const Logo = styled.div`
  padding: ${theme.spacing.xl};
  border-bottom: 1px solid ${theme.colors.surface};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const PScreenLogoContainer = styled.div`
  padding: ${theme.spacing.lg} ${theme.spacing.xl};
  border-bottom: 1px solid ${theme.colors.surface};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.background};
`;

const KajomLogoContainer = styled.div`
  padding: ${theme.spacing.lg} ${theme.spacing.xl};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
`;

const LogoImage = styled.img`
  max-width: 100%;
  height: auto;
  max-height: 80px;
  object-fit: contain;
`;

const LogoSubtext = styled.p`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
  margin: ${theme.spacing.sm} 0 0 0;
  text-align: center;
`;

const Navigation = styled.nav`
  flex: 1;
  padding: ${theme.spacing.lg} 0;
  overflow-y: auto;
`;

const NavSection = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const NavSectionTitle = styled.div`
  padding: 0 ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.sm};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text}60;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg}; /* 16px 24px - espacement 20-24px */
  margin-bottom: ${theme.spacing.xs};
  color: ${theme.colors.text}; /* Noir profond #191919 */
  text-decoration: none;
  transition: all ${theme.transitions.fast};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.medium}; /* Medium */
  text-transform: uppercase;
  letter-spacing: 0.5px; /* Capitales espacées */
  position: relative;
  border-left: 3px solid transparent;

  svg {
    font-size: 18px;
    stroke-width: 1.8px; /* Icônes fines */
  }

  &:hover {
    background: ${theme.colors.background}; /* Blanc cassé #F0F3F2 */
    color: ${theme.colors.text};
  }

  &.active {
    background: transparent;
    color: ${theme.colors.primary}; /* Texte vert sauge #6A8A82 */
    font-weight: ${theme.typography.weights.medium};
    border-left: 3px solid ${theme.colors.accent}; /* Ligne cuivrée #B87333 */

    svg {
      color: ${theme.colors.primary};
    }
  }
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xs};
  color: ${theme.colors.text};
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: relative;
  border-left: 3px solid transparent;
  width: 100%;
  text-align: left;
  font-family: ${theme.typography.fontFamily};

  svg {
    font-size: 18px;
    stroke-width: 1.8px;
  }

  &:hover {
    background: ${theme.colors.background};
    color: ${theme.colors.text};
  }
`;

const UserSection = styled.div`
  padding: ${theme.spacing.lg};
  border-top: 1px solid ${theme.colors.surface};
  margin-top: auto;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${theme.colors.primary};
  color: ${theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${theme.typography.weights.bold};
  font-size: ${theme.typography.sizes.base};
`;

const UserDetails = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
`;

const UserRole = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
`;

const LogoutButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  background: ${theme.colors.surface};
  border: none;
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text};
  font-size: ${theme.typography.sizes.sm};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.error}10;
    color: ${theme.colors.error};
  }
`;

const MobileToggle = styled.button`
  display: none;
  position: fixed;
  top: ${theme.spacing.lg};
  left: ${theme.spacing.lg};
  z-index: 1001;
  background: ${theme.colors.white};
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm};
  box-shadow: ${theme.shadows.md};
  cursor: pointer;
  color: ${theme.colors.primary};
  font-size: 24px;

  @media (max-width: ${theme.breakpoints.lg}) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;


type SidebarNavItem = {
      to?: string;
      icon: React.ReactNode;
      label: string;
      type?: 'link' | 'button' | string;
      onClick?: () => void;
    };


export const Sidebar: React.FC = () => {
  const { loggedUser: user, logout } = useSession();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);

  const getNavItems = (): SidebarNavItem[] => {

    

    let navbar_links: SidebarNavItem[] = [];


    navbar_links = [...navbar_links, { to: '/dashboard', icon: <FiHome />, label: 'Tableau de bord', type: 'link' }]

    if (user?.role_codes?.includes('agent')) {
      navbar_links = [...navbar_links,
      { to: '/agent/new-folder', icon: <FiClipboard />, label: 'Dossiers de candidature', type: 'link' },
      { to: '/agent/pending', icon: <FiClipboard />, label: 'Candidatures à traiter', type: 'link' },
     
      // { to: '/agent/communication', icon: <FiMessageCircle />, label: 'Communication', type: 'link' },
     
      ]


      if (!user?.role_codes?.includes('manager')) {
        navbar_links = [...navbar_links,  { to: '/agent/verified', icon: <FiCheckCircle />, label: 'Candidatures vérifiées', type: 'link' },]
      }
    }


    if (user?.role_codes?.includes('manager')) {
      navbar_links = [
        ...navbar_links,
        { to: '/manager/verified', icon: <FiCheckCircle />, label: 'Candidatures vérifiées', type: 'link' },
        { to: '/manager/preselected', icon: <FiUser />, label: 'Candidats présélectionnés', type: 'link' },
        // { to: '/manager/scoring', icon: <FiTrendingUp />, label: 'Supervision Scoring', type: 'link' },
        { to: '/manager/scored', icon: <FiClipboard />, label: 'Candidats scorés', type: 'link' },
        { to: '/manager/team', icon: <FiUsers />, label: 'Gestion Équipe', type: 'link' },
        { to: '/manager/reports', icon: <FiBarChart2 />, label: 'Rapports', type: 'link' },
      ]
    }
    
    navbar_links = [...navbar_links,  { icon: <FiCalendar />, label: 'Calendrier Commissions', type: 'button', onClick: () => setIsCalendarModalOpen(true) }]

    if (user?.role_codes?.includes('commission')) {
      navbar_links = [
        ...navbar_links,
        { to: '/commission', icon: <FiHome />, label: 'Sessions' },
        { to: '/commission/history', icon: <FiCalendar />, label: 'Calendrier Commissions' },
      ]
    }
    if (user?.role_codes?.includes('admin')) {
      navbar_links = [...navbar_links,

        // { to: '/superadmin', icon: <FiHome />, label: 'Dashboard' },
        // { to: '/superadmin/licenses', icon: <FiShield />, label: 'Licences' },
        // { to: '/superadmin/subscriptions', icon: <FiCheckCircle />, label: 'Abonnements' },
        // { to: '/superadmin/revenue', icon: <FiDollarSign />, label: 'Revenus' },

        // { to: '/admin', icon: <FiHome />, label: 'Tableau de bord' },
        { to: '/admin/users', icon: <FiUsers />, label: 'Utilisateurs' },
        { to: '/admin/housing', icon: <FiHome />, label: 'Gestion Logements' },
        // { to: '/admin/landing', icon: <FiFileText />, label: 'Landing Page Candidat' },
        { to: '/admin/config', icon: <FiSettings />, label: 'Configuration' },
        { to: '/admin/billing', icon: <FiCreditCard />, label: 'Facturation' },
        // { to: '/admin/compliance', icon: <FiShield />, label: 'Conformité' },
        { to: '/admin/analytics', icon: <FiActivity />, label: 'Analytics' },
      ]
    }


    if (user?.role_codes?.includes('candidate')) {
      return ([
        { to: '/candidate-home', icon: <FiHome />, label: 'Mon Score' },
        { to: '/mon-dossier', icon: <FiFileText />, label: 'Mon Dossier' },
        { to: '/applications', icon: <FiFileText />, label: 'Mes Candidatures' },
        { to: '/profile', icon: <FiUser />, label: 'Profil' },
        { to: '/documents', icon: <FiFolder />, label: 'Documents' },
        { to: '/messages', icon: <FiMessageCircle />, label: 'Messages' },
        { to: '/housing-search', icon: <FiSearch />, label: 'Recherche Logement' },
      ] as SidebarNavItem[])
    }


    return navbar_links

   
  };

  // Déterminer si on est dans une route candidat
  const isCandidateRoute = location.pathname.startsWith('/candidate-home') ||
    location.pathname.startsWith('/applications') ||
    location.pathname.startsWith('/profile') ||
    location.pathname.startsWith('/documents') ||
    location.pathname.startsWith('/messages') ||
    location.pathname.startsWith('/housing-search') ||
    location.pathname.startsWith('/new-application');

  return (
    <>
      <MobileToggle onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <FiX /> : <FiMenu />}
      </MobileToggle>

      <SidebarContainer $isOpen={isOpen}>
        {isCandidateRoute ? (
          <KajomLogoContainer>
            <LogoImage src={logoKajom} alt="Kajom Capital" />
          </KajomLogoContainer>
        ) : (
          <PScreenLogoContainer>
            <LogoImage src={logoPscreen} alt="P-Screen" />
            <LogoSubtext>by Praedium Tech</LogoSubtext>
          </PScreenLogoContainer>
        )}

        <Navigation>
          <NavSection>
            <NavSectionTitle>Navigation</NavSectionTitle>
            {getNavItems().map((item, index) => {
              // Button items must be rendered as a button
              if (item.type === 'button') {
                return (
                  <NavButton
                    key={item.label + index}
                    onClick={() => {
                      setIsOpen(false);
                      item.onClick?.();
                    }}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </NavButton>
                );
              }

              // Links require a `to` prop — if missing, skip rendering
              if (!item.to) return null;

              return (
                <NavItem
                  key={item.to ?? item.label + index}
                  to={item.to}
                  onClick={() => setIsOpen(false)}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </NavItem>
              );
            })}
          </NavSection>
        </Navigation>

        <UserSection>
          <UserInfo>
            <UserAvatar>
              {user?.first_name?.charAt(0)}
              {user?.last_name?.charAt(0)}
            </UserAvatar>
            <UserDetails>
              <UserName>
                {user?.first_name} {user?.last_name}
              </UserName>
              <UserRole>{user?.role_codes?.map((item)=>(<>{`${item} `}</>))}</UserRole>
            </UserDetails>
          </UserInfo>
          <LogoutButton onClick={logout}>
            <FiLogOut />
            Déconnexion
          </LogoutButton>
        </UserSection>
      </SidebarContainer>

      <CommissionCalendarModal
        isOpen={isCalendarModalOpen}
        onClose={() => setIsCalendarModalOpen(false)}
      />
    </>
  );
};
